import React, { useState, useEffect, useCallback } from 'react';
import { AppBar, Toolbar, IconButton, Box, Avatar, Badge, Menu, MenuItem, Tooltip, useMediaQuery, useTheme, Snackbar, Alert, Divider } from '@mui/material';
import { Bot, LogOut, Menu as MenuIcon, X, Download, MessageCircle, Bell } from 'lucide-react';
import TranslateIcon from '@mui/icons-material/Translate';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, useTranslation } from '../../i18n';
import InternalAssistant from '../internal_assistant/InternalAssistant';
import Messaging from '../internal_messages/Messaging';
import NotificationSettings from './NotificationSettings';
// Notification settings removed

// Add debug flag - set to false to disable all noisy console logging
const DEBUG_MODE = false;

// Add debug logging function
const debugLog = (...args) => {
  if (DEBUG_MODE) {
    //console.log(...args);
  }
};


async function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const Header = ({ 
  toggleSidebar, 
  isSidebarOpen, 
  darkMode,
}) => {
  const { t, i18n } = useTranslation(['layout', 'common']);
  const { logoutUser, user } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Push notifications removed
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState({ total_unread: 0, chat_rooms: {} });
  const [messagingMobileView, setMessagingMobileView] = useState(false);
  const [ws, setWs] = useState(null);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);

  // Push notifications removed

  // Push notifications removed

  

  useEffect(() => {
    // Fetch unread counts on component mount
    //();
    
    // Set up interval to periodically check for new messages
    //const interval = setInterval(, 5000); // Check every 5 seconds
    
    // Don't try to clear an undefined interval
    return () => {
      // clearInterval(interval);
    }; 
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLanguageMenuOpen = (event) => setLanguageAnchorEl(event.currentTarget);
  const handleLanguageMenuClose = () => setLanguageAnchorEl(null);

  const handleAssistantToggle = () => {
    setAssistantOpen(!assistantOpen);
  };

  const handleMessageToggle = () => {
    setMessagingOpen(!messagingOpen);
    if (!messagingOpen) {
      setMessagingMobileView(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleNotificationSettingsOpen = () => {
    setNotificationSettingsOpen(true);
    handleMenuClose();
  };

  const handleLanguageChange = async (language) => {
    await i18n.changeLanguage(language);
    handleLanguageMenuClose();
    handleMenuClose();
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          borderBottom: 'none',
          height: { xs: 64, md: 80 },
          transition: 'all 0.3s ease',
          px: { xs: 1, md: 2 },
          pt: { xs: 1, md: 1.5 },
        }}
      >
        <Box
          sx={{
            backgroundColor: darkMode ? 'rgba(17, 17, 17, 0.60)' : 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(12px) saturate(160%)',
            WebkitBackdropFilter: 'blur(12px) saturate(160%)',
            borderRadius: '16px',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
            boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.5)' : '0 6px 24px rgba(15,23,42,0.08)',
            height: { xs: 56, md: 64 },
            display: 'flex',
            alignItems: 'center',
            px: { xs: 2, md: 3 },
            color: darkMode ? '#FFFFFF' : '#0f172a',
          }}
        >
        <Toolbar sx={{ justifyContent: 'space-between', height: '100%', width: '100%', minHeight: '100% !important', px: '0 !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label={t('header.toggleSidebarAria', { ns: 'layout' })}
              onClick={toggleSidebar}
              edge="start"
              sx={{ mr: 2, display: { md: 'none' }, color: 'inherit' }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={isSidebarOpen ? 'open' : 'closed'}
                  initial={{ opacity: 0, rotate: -180 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 180 }}
                  transition={{ duration: 0.3 }}
                >
                  {isSidebarOpen ? <X size={24} /> : <MenuIcon size={24} />}
                </motion.div>
              </AnimatePresence>
            </IconButton>
            <motion.img 
              src={darkMode ? "/AitomotiveLab_White.svg" : "/AitomotiveLab.svg"}
              alt="AITomotive Logo" 
              style={{ height: isMobile ? '30px' : '40px', maxWidth: isMobile ? '120px' : '160px' }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, color: 'inherit' }}>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Tooltip title={t('header.messages', { ns: 'layout' })}>
                <IconButton color="inherit" onClick={handleMessageToggle} sx={{ color: 'inherit' }}>
                  <Badge 
                    badgeContent={unreadMessages.total_unread} 
                    color="error"
                    invisible={!unreadMessages || unreadMessages.total_unread === 0}
                  >
                    <MessageCircle size={24} />
                  </Badge>
                </IconButton>
              </Tooltip>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Tooltip title={t('header.assistant', { ns: 'layout' })}>
                <IconButton color="inherit" onClick={handleAssistantToggle} sx={{ color: 'inherit' }}>
                  <Bot size={24} />
                </IconButton>
              </Tooltip>
            </motion.div>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Tooltip title={t('header.userMenu', { ns: 'layout' })}>
                <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                  <Avatar
                    alt={user?.name || 'User'}
                    src={user?.avatar}
                    sx={{
                      width: 40,
                      height: 40,
                      border: '2px solid',
                      borderColor: darkMode ? '#4CC9F0' : '#4361EE',
                      boxShadow: darkMode ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(15,23,42,0.15)',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </motion.div>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  backgroundColor: darkMode ? '#1E1E1E' : '#FFFFFF',
                  color: darkMode ? '#FFFFFF' : '#333333',
                  border: `1px solid ${darkMode ? '#ffffff20' : '#00000020'}`,
                  borderRadius: 12,
                  backdropFilter: 'blur(6px)',
                  '& .MuiMenuItem-root': {
                    transition: 'background-color 0.3s',
                    '&:hover': {
                      backgroundColor: darkMode ? '#ffffff20' : '#00000010',
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleNotificationSettingsOpen}>
                <Bell size={16} style={{ marginRight: 8 }} />
                {t('header.notifications', { ns: 'layout' })}
              </MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); navigate('/install-guide'); }}>
                <Download size={16} style={{ marginRight: 8 }} />
                {t('header.installApp', { ns: 'layout' })}
              </MenuItem>
              <MenuItem onClick={handleLanguageMenuOpen}>
                <TranslateIcon fontSize="small" style={{ marginRight: 8 }} />
                {t('header.language', { ns: 'layout' })}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleMenuClose(); logoutUser(); }}>
                <LogOut size={16} style={{ marginRight: 8 }} />
                {t('header.logout', { ns: 'layout' })}
              </MenuItem>
            </Menu>
            <Menu
              anchorEl={languageAnchorEl}
              open={Boolean(languageAnchorEl)}
              onClose={handleLanguageMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  backgroundColor: darkMode ? '#1E1E1E' : '#FFFFFF',
                  color: darkMode ? '#FFFFFF' : '#333333',
                  border: `1px solid ${darkMode ? '#ffffff20' : '#00000020'}`,
                  borderRadius: 12,
                  backdropFilter: 'blur(6px)',
                  '& .MuiMenuItem-root': {
                    transition: 'background-color 0.3s',
                    '&:hover': {
                      backgroundColor: darkMode ? '#ffffff20' : '#00000010',
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
              {SUPPORTED_LANGUAGES.map((language) => (
                <MenuItem key={language} onClick={() => handleLanguageChange(language)}>
                  {i18n.resolvedLanguage === language ? '• ' : ''}
                  {t(`language.${language}`, { ns: 'common' })}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
        </Box>
      </AppBar>

      <InternalAssistant 
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        darkMode={darkMode}
      />

      <NotificationSettings 
        open={notificationSettingsOpen}
        onClose={() => setNotificationSettingsOpen(false)}
        darkMode={darkMode}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;
